import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { debounce } from "lodash";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

import {
  Box,
  Typography,
  IconButton,
  Checkbox,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
  TextField,
  InputAdornment,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Close,
  Check,
  DragIndicator,
  SearchOutlined,
  ClearOutlined,
  EditOutlined,
} from "@mui/icons-material";
import { ReactComponent as NounLink } from "../../../assets/noun-link.svg";

import Thumbnail from "./thumbnail";

export default function SlideManagement(props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [slides, setSlides] = useState(props.presentation?.slides || []);
  const [selectedSlides, setSelectedSlides] = useState([]);
  const [search, setSearch] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const selectedAction = "";

  const reorderQuietRef = useRef(props.reorderSlidesQuietly);
  useEffect(() => {
    reorderQuietRef.current = props.reorderSlidesQuietly;
  }, [props.reorderSlidesQuietly]);

  // Sync local order when the upstream presentation slides change (e.g. after
  // the parent reloads the deck post-save). Skip while we're mid-save so the
  // user's drag isn't snapped back before the server response lands.
  useEffect(() => {
    if (savingOrder) return;
    setSlides(props.presentation?.slides || []);
  }, [props.presentation?.slides, savingOrder]);

  const indexhRef = useRef(props.indexh);
  useEffect(() => {
    indexhRef.current = props.indexh;
  }, [props.indexh]);

  const debouncedSaveOrder = useMemo(
    () =>
      debounce((orderIds, newActiveIndex) => {
        const quiet = reorderQuietRef.current;
        const promise = quiet
          ? Promise.resolve(quiet(orderIds, newActiveIndex))
          : Promise.resolve();
        promise.finally(() => setSavingOrder(false));
      }, 600),
    [],
  );

  useEffect(() => {
    return () => debouncedSaveOrder.cancel();
  }, [debouncedSaveOrder]);

  // Reset
  useEffect(() => {
    if (!props.open) {
      setSelectedSlides([]);
      setSearch("");
    }
  }, [props.open]);

  useEffect(() => {
    if (!props.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        props.onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [props.open, props]);

  const filteredSlides = useMemo(() => {
    if (!search.trim()) return slides;
    const q = search.toLowerCase();
    return slides.filter(
      (s) =>
        (s.title || "").toLowerCase().includes(q) ||
        String(s.id).includes(q),
    );
  }, [slides, search]);

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSlides((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      // Track where the slide the editor is currently viewing ends up after
      // the move, so the parent can re-point reveal + indexh at it instead of
      // staying on whatever slide happens to occupy the old index.
      const activeSlideId = prev[indexhRef.current]?.id;
      const newActiveIndex =
        activeSlideId != null
          ? next.findIndex((s) => s.id === activeSlideId)
          : -1;
      setSavingOrder(true);
      debouncedSaveOrder(
        next.map((s) => s.id),
        newActiveIndex >= 0 ? newActiveIndex : indexhRef.current,
      );
      return next;
    });
  }

  const handleToggleSlide = (id) => {
    setSelectedSlides((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const handleRenameSlide = useCallback(
    (slideId, title) => {
      // Optimistic local update
      setSlides((prev) =>
        prev.map((s) => (s.id === slideId ? { ...s, title } : s)),
      );
      const result = props.onRenameSlide && props.onRenameSlide(slideId, title);
      return result && typeof result.then === "function"
        ? result
        : Promise.resolve();
    },
    [props],
  );

  const handleActionChange = (event) => {
    if (selectedSlides.length === 0) return;
    const value = event.target.value;

    if (value === "delete") {
      props.onRemoveSlides(selectedSlides);
      setSelectedSlides([]);
    }
    if (value === "draft") {
      props.toggleStatus(selectedSlides);
      setSelectedSlides([]);
    }
    if (value === "thumbnail") {
      props.generateThumbnails(selectedSlides);
      setSnackbarOpen(true);
      setSelectedSlides([]);
    }
  };

  if (!props.open) return null;

  return (
    <>
      <Box
        className="slide-management-modal"
        role="dialog"
        aria-label="Slide management"
      >
        <Box className="sm-header">
          <Box className="sm-header__title">
            <Typography variant="h6" owner="admin" color="adminText.dark">
              Manage Slides
            </Typography>
            <Typography variant="body2" color="adminText.main" className="ms-2">
              {slides.length} slide{slides.length === 1 ? "" : "s"}
              {selectedSlides.length > 0 &&
                ` · ${selectedSlides.length} selected`}
            </Typography>
          </Box>

          <Box className="sm-header__actions">
            <FormControl size="small" className="sm-actions-select">
              <InputLabel sx={{ fontSize: 14 }} id="sm-actions-label">
                Actions
              </InputLabel>
              <Select
                labelId="sm-actions-label"
                id="sm-actions"
                value={selectedAction}
                label="Actions"
                disabled={selectedSlides.length === 0}
                variant="outlined"
                onChange={handleActionChange}
                input={
                  <OutlinedInput sx={{ fontSize: 14 }} label="Actions" />
                }
              >
                <MenuItem value={"thumbnail"} sx={{ fontSize: 14 }}>
                  Generate Thumbnail
                </MenuItem>
                <MenuItem value={"draft"} sx={{ fontSize: 14 }}>
                  Toggle Hide
                </MenuItem>
                <MenuItem value={"delete"} sx={{ fontSize: 14 }}>
                  Delete
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              className="sm-search"
              size="small"
              placeholder="Search slides…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch("")}
                      aria-label="Clear search"
                    >
                      <ClearOutlined fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {savingOrder && (
              <Typography
                variant="caption"
                color="adminText.main"
                className="sm-saving"
              >
                Saving order…
              </Typography>
            )}

            <IconButton
              onClick={props.onClose}
              className="sm-close-btn"
              aria-label="Close slide management"
            >
              <Close />
            </IconButton>
          </Box>
        </Box>

        <Box className="sm-body">
          {filteredSlides.length === 0 ? (
            <Box className="sm-empty">
              <Typography variant="body1" color="adminText.main">
                {search
                  ? "No slides match your search."
                  : "This presentation has no slides yet."}
              </Typography>
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToWindowEdges]}
            >
              <SortableContext
                items={filteredSlides.map((s) => s.id)}
                strategy={rectSortingStrategy}
              >
                <Box className="sm-grid">
                  {filteredSlides.map((slide) => {
                    const originalIndex = slides.findIndex(
                      (s) => s.id === slide.id,
                    );
                    const isSelected = selectedSlides.includes(slide.id);
                    const isLibrarySlide =
                      slide.initial_presentation !== props.presentation.id;
                    return (
                      <SortableSlideCard
                        key={`mgmt-${slide.id}`}
                        id={slide.id}
                        slide={slide}
                        index={originalIndex}
                        isActive={originalIndex === props.indexh}
                        isSelected={isSelected}
                        isLibrarySlide={isLibrarySlide}
                        presentation={{ id: props.presentation.id }}
                        thumbnailVersion={
                          props.thumbnailVersions?.[slide.id]
                        }
                        onToggleSelect={() => handleToggleSlide(slide.id)}
                        onRename={(title) =>
                          handleRenameSlide(slide.id, title)
                        }
                        onClickThumb={() => {
                          props.navigateTo(originalIndex);
                          props.onClose();
                        }}
                        isDragDisabled={selectedSlides.length > 0}
                      />
                    );
                  })}
                </Box>
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          <Typography variant="body">
            Selected thumbnails will be generated shortly!
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
}

function SortableSlideCard(props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.id, disabled: props.isDragDisabled });

  const [draftTitle, setDraftTitle] = useState(props.slide.title || "");
  const [titleStatus, setTitleStatus] = useState("idle"); // idle | saving | saved | error
  const lastCommittedRef = useRef(props.slide.title || "");
  const savedTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  // Keep the local draft in sync when the upstream slide title changes
  // (e.g. after a server refresh) — but only when the user isn't actively
  // typing a different value.
  useEffect(() => {
    const incoming = props.slide.title || "";
    if (incoming !== lastCommittedRef.current) {
      setDraftTitle(incoming);
      lastCommittedRef.current = incoming;
    }
  }, [props.slide.title]);

  const onRenameRef = useRef(props.onRename);
  useEffect(() => {
    onRenameRef.current = props.onRename;
  }, [props.onRename]);

  const debouncedRename = useMemo(
    () =>
      debounce((value) => {
        if (value === lastCommittedRef.current) return;
        lastCommittedRef.current = value;
        if (!onRenameRef.current) return;
        setTitleStatus("saving");
        Promise.resolve(onRenameRef.current(value))
          .then(() => {
            if (!isMountedRef.current) return;
            setTitleStatus("saved");
            if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
            savedTimerRef.current = setTimeout(() => {
              if (isMountedRef.current) setTitleStatus("idle");
            }, 1500);
          })
          .catch(() => {
            if (isMountedRef.current) setTitleStatus("error");
          });
      }, 600),
    [],
  );

  useEffect(() => {
    return () => debouncedRename.cancel();
  }, [debouncedRename]);

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setDraftTitle(value);
    debouncedRename(value);
  };

  const handleTitleBlur = () => {
    debouncedRename.flush();
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isDraft = props.slide.published !== true;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={`sm-card ${props.isActive ? "active" : ""} ${
        props.isSelected ? "selected" : ""
      } ${isDraft ? "draft" : ""}`}
      data-id={props.id}
    >
      <Tooltip
        title={`S=${props.slide.id} (${props.slide.title || ""})`}
        placement="top"
      >
        <Box className="sm-card__thumb">
          {props.isLibrarySlide && (
            <div className="library-slide-indicator">
              <span className="icon-container">
                <span className="icon">
                  <NounLink
                    style={{
                      transform: "rotate(45deg)",
                      pointerEvents: "none",
                    }}
                  />
                </span>
                <span className="tri" />
              </span>
            </div>
          )}
          <Tooltip title="Open in editor" placement="top">
            <IconButton
              className={`sm-card__edit-btn ${
                props.isLibrarySlide ? "sm-card__edit-btn--shifted" : ""
              }`}
              size="small"
              onClick={props.onClickThumb}
              aria-label="Open slide in editor"
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          {isDraft ? (
            <Box className="sm-card__draft-placeholder">
              <Typography variant="caption">Hidden</Typography>
            </Box>
          ) : (
            <Thumbnail
              isSlide={true}
              isActive={props.isActive}
              slide={props.slide}
              presentation={props.presentation}
              version={props.thumbnailVersion}
            />
          )}
        </Box>
      </Tooltip>

      <Box className="sm-card__title">
        <TextField
          size="small"
          variant="outlined"
          fullWidth
          placeholder="Add slide title"
          value={draftTitle}
          onChange={handleTitleChange}
          onBlur={handleTitleBlur}
          inputProps={{ "aria-label": "Slide title" }}
          error={titleStatus === "error"}
          helperText={
            titleStatus === "error" ? "Couldn't save — try again" : undefined
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" className="sm-card__title-status">
                {titleStatus === "saving" && (
                  <CircularProgress size={12} thickness={5} />
                )}
                {titleStatus === "saved" && (
                  <Check
                    sx={{ fontSize: 14 }}
                    className="sm-card__title-saved"
                  />
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box className="sm-card__bottombar">
        <Box className="sm-card__index">{props.index + 1}</Box>
        <Box
          className="sm-card__drag"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <DragIndicator fontSize="small" />
        </Box>
        <Box className="sm-card__check">
          <Checkbox
            checked={props.isSelected}
            sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
            onChange={props.onToggleSelect}
          />
        </Box>
      </Box>
    </Box>
  );
}
